import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const OVERLAY_LIST_MAX_PX = 416; /* ~26rem; enough for long option lists */
const OVERLAY_GAP_PX = 4;
const OVERLAY_EDGE_PX = 8;
const OVERLAY_MIN_SPACE_TO_FLIP_PX = 200;
const OVERLAY_Z_INDEX = 10000;

// Turns the `listPosition` a useDropdownOverlay caller gets back into the
// inline `style` its portaled list needs - every overlay-mode dropdown
// (DropdownActions, PhaseTemplateDropdown) computes this same shape from the
// same source, so it lives here instead of being re-derived per caller.
// Safe to call before the first measurement (listPosition still null).
export function getOverlayStyle(listPosition) {
  if (!listPosition) return { position: "fixed", zIndex: OVERLAY_Z_INDEX };

  return {
    position: "fixed",
    ...(listPosition.top != null ? { top: listPosition.top } : {}),
    ...(listPosition.bottom != null ? { bottom: listPosition.bottom } : {}),
    ...(listPosition.left != null ? { left: listPosition.left } : {}),
    ...(listPosition.right != null ? { right: listPosition.right } : {}),
    minWidth: listPosition.minWidth,
    maxHeight: listPosition.maxHeight,
    zIndex: OVERLAY_Z_INDEX,
  };
}

/** Function to compute the position of the dropdown list when overlay is true.
 * @param {{
 *   rect: DOMRect,
 *   listWidth: number,
 *   alignRight: boolean,
 *   openUpward: boolean,
 * }} params
 */
export function computeOverlayListPosition({
  rect,
  listWidth,
  alignRight,
  openUpward,
}) {
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const width = Math.max(rect.width, listWidth);

  const spaceBelow = Math.max(
    0,
    viewportH - rect.bottom - OVERLAY_GAP_PX - OVERLAY_EDGE_PX
  );
  const spaceAbove = Math.max(0, rect.top - OVERLAY_GAP_PX - OVERLAY_EDGE_PX);

  const listOpensUpward =
    openUpward ||
    (spaceBelow < OVERLAY_MIN_SPACE_TO_FLIP_PX && spaceAbove > spaceBelow);

  const vertical = listOpensUpward
    ? {
        top: null,
        bottom: viewportH - rect.top + OVERLAY_GAP_PX,
        maxHeight: Math.min(OVERLAY_LIST_MAX_PX, spaceAbove),
      }
    : {
        top: rect.bottom + OVERLAY_GAP_PX,
        bottom: null,
        maxHeight: Math.min(OVERLAY_LIST_MAX_PX, spaceBelow),
      };

  let left = null;
  let right = null;

  if (alignRight) {
    right = viewportW - rect.right;
    const listLeft = viewportW - right - width;
    if (listLeft < OVERLAY_EDGE_PX) {
      right = null;
      left = Math.max(OVERLAY_EDGE_PX, viewportW - width - OVERLAY_EDGE_PX);
    }
  } else {
    left = rect.left;
    if (left + width > viewportW - OVERLAY_EDGE_PX) {
      left = Math.max(OVERLAY_EDGE_PX, viewportW - width - OVERLAY_EDGE_PX);
    }
    if (left < OVERLAY_EDGE_PX) {
      left = OVERLAY_EDGE_PX;
    }
  }

  return {
    ...vertical,
    left,
    right,
    minWidth: rect.width,
  };
}

// Shared open/close + fixed-position overlay placement, extracted from
// parts/dropdown.js so dropdown-shaped components (Dropdown, DropdownActions)
// don't duplicate this positioning/click-outside/scroll-close logic — only
// their own summary/list JSX and per-option rendering differ.
//
// `remeasureKey` mirrors what dropdown.js used to pass as `options` in its
// positioning effect's deps: any value whose change should trigger a
// re-measure of the list's position (e.g. because the option count changed
// the list's height). Callers pass whatever their own "list contents changed"
// signal is.
export function useDropdownOverlay({
  overlay = false,
  alignRight = false,
  openUpward = false,
  remeasureKey,
} = {}) {
  const [opened, setOpened] = useState(false);
  const [listPosition, setListPosition] = useState(null);
  const summaryRef = useRef(null);
  const listRef = useRef(null);

  const close = useCallback(() => setOpened(false), []);
  const toggle = useCallback((e) => {
    e.preventDefault();
    setOpened((o) => !o);
  }, []);

  // When overlay is true and opened, position the list from the summary.
  // maxHeight uses space below (or above if flipped)—not 100vh—so the list
  // stays on-screen and overflow-y scrolling works. Horizontal position clamps
  // to the viewport; remeasures once the portaled list has laid out.
  // biome-ignore lint/correctness/useExhaustiveDependencies: remeasureKey is a caller-supplied re-measure trigger, not a value read in this closure
  useLayoutEffect(() => {
    if (!overlay || !opened || !summaryRef.current) {
      setListPosition(null);
      return undefined;
    }

    /** Function to place the dropdown list when overlay is true. */
    const placeList = () => {
      const rect = summaryRef.current.getBoundingClientRect();
      const listWidth = listRef.current?.getBoundingClientRect().width ?? rect.width;
      setListPosition(
        computeOverlayListPosition({
          rect,
          listWidth,
          alignRight: Boolean(alignRight),
          openUpward,
        })
      );
    };

    placeList();

    if (!listRef.current) {
      const frameId = requestAnimationFrame(placeList);
      return () => cancelAnimationFrame(frameId);
    }

    return undefined;
  }, [overlay, opened, openUpward, alignRight, remeasureKey]);

  // Close on click outside (when overlay)
  useEffect(() => {
    if (!overlay || !opened) return undefined;
    const handleClickOutside = (e) => {
      if (
        summaryRef.current &&
        !summaryRef.current.contains(e.target) &&
        listRef.current &&
        !listRef.current.contains(e.target)
      ) {
        close();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [overlay, opened, close]);

  // Close on scroll (outside the list) or resize when overlay
  useEffect(() => {
    if (!overlay || !opened) return undefined;
    const handleScroll = (e) => {
      if (listRef.current?.contains(e.target)) return;
      close();
    };
    const handleResize = () => close();
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [overlay, opened, close]);

  return { opened, setOpened, toggle, close, summaryRef, listRef, listPosition };
}

export default useDropdownOverlay;
